import {
  CounterBtn,
  CounterWrap,
  TravellerCountErrorWrap,
  TravellerCountInputField,
  TravellerCountInputWrap,
  TravellerCountWigetStyle,
} from "./TravellingToWidget.style";

import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getAnalytics } from "@tm/insurance/analytics";
import { FlexComp, SpanTag } from "@tm/static";
import { theme } from "@tm/utility/global";
import FormattedText, { TextNodeType } from "../FormattedText/FormattedText";

interface TravellerCountWigetProps {
  showTravCount?: boolean;
  handleCaptureCountIncr?: () => void;
  captureCount?: number;
  ref?: (instance: HTMLDivElement | null) => void;
  travCountTitle?: TextNodeType | string;
  travCountSubtitle?: TextNodeType | string;
  travCountInfo?: TextNodeType | string;
  handleCaptureCountDecr?: () => void;
  maxTravAllowed: number;
  minTravAllowed: number;
  isInputEnabled?: boolean;
  handleCaptureCountChange?: (count: number) => void;
  travCountError?: TextNodeType | string;
  setIsTravCountError?: Dispatch<SetStateAction<boolean>>;
}

const TravellerCounterWiget = ({
  showTravCount,
  maxTravAllowed,
  minTravAllowed,
  handleCaptureCountIncr,
  handleCaptureCountDecr,
  captureCount,
  ref,
  travCountTitle,
  travCountSubtitle,
  travCountInfo,
  isInputEnabled,
  handleCaptureCountChange,
  travCountError,
  setIsTravCountError,
}: TravellerCountWigetProps) => {
  const analytics = getAnalytics();

  const initialValue = useMemo(() => {
    if (typeof window !== "undefined") {
      const urlTrav = new URLSearchParams(window.location.search).get(
        "travellers"
      );
      if (urlTrav && !Number.isNaN(parseInt(urlTrav))) {
        return urlTrav;
      }
    }
    return captureCount?.toString() ?? "";
  }, []);

  const [inputValue, setInputValue] = useState<string>(initialValue);
  const [showError, setShowError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (captureCount !== undefined && !isFocused) {
      setInputValue(captureCount.toString());
    }
  }, [captureCount]);

  const handleCaptureCountIncrWithAnalytics = () => {
    analytics?.trackLandingEvents({
      actionType: "number_of_travellers_increment_clicked",
    });
    handleCaptureCountIncr?.();
  };
  const handleCaptureCountDecrWithAnalytics = () => {
    analytics?.trackLandingEvents({
      actionType: "number_of_travellers_decrement_clicked",
    });
    handleCaptureCountDecr?.();
  };

  const updateErrorState = (hasError: boolean) => {
    setShowError(hasError);
    setIsTravCountError?.(hasError);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val === "") {
      setInputValue("");
      updateErrorState(false);
      return;
    }
    const numericVal = val.replace(/[^0-9]/g, "");
    setInputValue(numericVal);

    const parsed = parseInt(numericVal, 10);
    if (
      !isNaN(parsed) &&
      parsed >= minTravAllowed &&
      parsed <= maxTravAllowed
    ) {
      updateErrorState(false);
      handleCaptureCountChange?.(parsed);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    const parsed = parseInt(inputValue, 10);
    const isValid =
      !isNaN(parsed) && parsed >= minTravAllowed && parsed <= maxTravAllowed;

    if (isValid) {
      updateErrorState(false);
      setInputValue(parsed.toString());
      handleCaptureCountChange?.(parsed);
    } else {
      updateErrorState(true);
    }

    analytics?.trackLandingEvents({
      actionType: "number_of_travellers_input_changed",
    });
  };

  const handleWrapClick = () => {
    inputRef.current?.focus();
  };

  if (isInputEnabled) {
    return (
      <TravellerCountInputWrap isFocused={isFocused} onClick={handleWrapClick}>
        <SpanTag
          textTransform="uppercase"
          color={isFocused ? theme.blueText : theme.greyText}
          fontWeight={"bold"}
          fontSize="xs"
        >
          {travCountTitle && <FormattedText value={travCountTitle} />}
        </SpanTag>
        <FlexComp flexDirection="column">
          <TravellerCountInputField
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            min={minTravAllowed}
            max={maxTravAllowed}
            placeholder={minTravAllowed.toString()}
          />
          <SpanTag color={theme.greyText6} fontSize="xs">
            {travCountSubtitle && <FormattedText value={travCountSubtitle} />}
          </SpanTag>
        </FlexComp>
        {showError && travCountError && (
          <TravellerCountErrorWrap>
            <SpanTag color={theme.redText2} fontSize="xs">
              <FormattedText value={travCountError} />
            </SpanTag>
          </TravellerCountErrorWrap>
        )}
      </TravellerCountInputWrap>
    );
  }

  return (
    <TravellerCountWigetStyle>
      <FlexComp flexDirection="column">
        <SpanTag
          textTransform="uppercase"
          color={showTravCount ? theme.blueText : theme.greyText}
          fontWeight={"bold"}
          fontSize="xs"
        >
          {travCountTitle && <FormattedText value={travCountTitle} />}
        </SpanTag>
        <SpanTag color={theme.greyText6} fontSize="xs">
          {travCountSubtitle && <FormattedText value={travCountSubtitle} />}
        </SpanTag>
      </FlexComp>
      <CounterWrap>
        <CounterBtn
          isDiabled={captureCount == minTravAllowed}
          onClick={handleCaptureCountDecrWithAnalytics}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="24" height="24" fill="white" />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6 11.0938H17.5V12.9064H6V11.0938Z"
              fill="var(--tm-accent-color)"
            />
          </svg>
        </CounterBtn>
        <SpanTag fontWeight="bold" fontSize="md">
          {captureCount && captureCount <= 9
            ? "0" + captureCount
            : captureCount}
        </SpanTag>
        <CounterBtn
          isDiabled={captureCount == maxTravAllowed}
          onClick={handleCaptureCountIncrWithAnalytics}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <rect width="24" height="24" fill="white" />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12.612 6.04785V11.1719H17.496V12.7919H12.612V17.9519H10.86V12.7919H6V11.1719H10.86V6.04785H12.612Z"
              fill="var(--tm-accent-color)"
            />
          </svg>
        </CounterBtn>
      </CounterWrap>
    </TravellerCountWigetStyle>
  );
};

export default TravellerCounterWiget;
